import React, { useCallback, useEffect } from "react";
import { Provider } from "react-redux";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import {
    setClient,
    setProject,
    selectCurrentClient,
    selectCurrentProject,
    selectDeviceData,
    loadLatestClientAssetDataDataset,
    loadLatestDeviceDataDataset,
    resetClientAssetDataDataset,
    resetDeviceDataDataset,
    resetCurrentPosition,
} from "./store/reducers/deviceLinkingAppStoreSlice";
import { store } from "./store/store";
import makeStyles from "@mui/styles/makeStyles";
import ClientAssetDataAndDeviceMapView from "./ClientAssetDataAndDeviceMapView";
import ClientAssetDataAndDevicesList from "./ClientAssetDataAndDevicesList";
import DetailedViewControlPanel from "./DetailedViewControlPanel";
import { CARDINAL_DIRECTIONS } from "./AddOrEditDeviceForm";
import { ClientsDropdown, ProjectsDropdown } from "../DropdownV2";

/**
 * Converts any angle (in degrees) to a cardinal direction string.
 * Each direction covers a 45-degree range centered on its standard bearing.
 * e.g. N: -22.5 to 22.5, NE: 22.5 to 67.5, etc.
 */
function degreesToCardinalDirection(angle: number): string {
    const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
    // Normalize to 0–360
    const normalized = ((angle % 360) + 360) % 360;
    const index = Math.round(normalized / 45) % 8;
    return directions[index];
}
import OnlineDateOverrideInput from "./OnlineDateOverrideInput";
import { Button } from "@mui/material";
type DeviceClientAssetDataLinkingAppProps = {};

export default function DeviceClientAssetDataLinkingApp(props: DeviceClientAssetDataLinkingAppProps): JSX.Element {
    return (
        <Provider store={store}>
            <DeviceClientAssetDataLinkingAppContent {...props} />
        </Provider>
    );
}

const useStyles = makeStyles((theme) => ({
    appContainer: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    dropdownContainer: {
        display: "flex",
        flexDirection: "row",
        gap: "15px",
    },
    contentContainer: {
        flexGrow: 1,
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        gap: "30px",
        maxHeight: "calc(100vh - 275px)",
    },
    listPanel: {
        flex: 1.5,
        backgroundColor: "white",
        borderRadius: "10px",
        border: "1px solid gray",
    },
    mapPanel: {
        flex: 3,
    },
    controlPanel: {
        flex: 2,
    },
    onlineDateOverrideInput: {
        marginLeft: "auto",
        marginRight: "30px",
    },
}));

function DeviceClientAssetDataLinkingAppContent(props: DeviceClientAssetDataLinkingAppProps): JSX.Element {
    const classes = useStyles();
    const dispatch = useAppDispatch();

    const currentClient = useAppSelector(selectCurrentClient);
    const currentProject = useAppSelector(selectCurrentProject);
    const deviceData = useAppSelector(selectDeviceData);

    useEffect(() => {
        if (!currentClient || !currentProject) {
            dispatch(resetClientAssetDataDataset());
            dispatch(resetDeviceDataDataset());
            dispatch(resetCurrentPosition());
            return;
        }
        dispatch(loadLatestClientAssetDataDataset());
        dispatch(loadLatestDeviceDataDataset());
    }, [currentClient, currentProject]);

    const handleExportCSV = useCallback(() => {
        if (!deviceData) return;

        // Helper function to flatten nested objects
        const flattenObject = (obj: any, prefix = ""): any => {
            return Object.keys(obj).reduce((acc: any, key: string) => {
                const value = obj[key];
                const newKey = prefix ? `${prefix}.${key}` : key;

                // Special handling for visible_spans array
                if (key === "visible_spans" && Array.isArray(value) && value.length > 0) {
                    const targetedSpans = value.filter((s: any) => s.targeted_by_sensor);
                    acc["start_tower"] = Array.from(new Set(targetedSpans.map((s: any) => s.start_structure).filter(Boolean))).join(", ");
                    acc["end_tower"] = Array.from(new Set(targetedSpans.map((s: any) => s.end_structure).filter(Boolean))).join(", ");
                    acc["section_name"] = Array.from(new Set(targetedSpans.map((s: any) => s.section_name).filter(Boolean))).join(", ");
                    // Also keep the full visible_spans array
                    acc[newKey] = JSON.stringify(value);
                } else if (value && typeof value === "object" && !Array.isArray(value)) {
                    Object.assign(acc, flattenObject(value, newKey));
                } else if (Array.isArray(value)) {
                    acc[newKey] = JSON.stringify(value);
                } else {
                    acc[newKey] = value;
                }
                return acc;
            }, {});
        };

        // Convert devices array to CSV with prefix
        const devicesFlat = deviceData.data.devices.map((device) => {
            const flat = flattenObject(device);
            const prefixed: any = {};
            Object.keys(flat).forEach((key) => {
                prefixed[`edge_device_${key}`] = flat[key];
            });
            if (device.approx_angle !== undefined && device.approx_angle !== null) {
                prefixed["edge_device_approx_angle_cardinal"] = degreesToCardinalDirection(device.approx_angle);
            }
            return prefixed;
        });

        // Convert weather sensors array to CSV with prefix
        const degreesToCardinal = Object.fromEntries(
            CARDINAL_DIRECTIONS.map(({ value, label }) => [value, label])
        );
        const weatherSensorsFlat = deviceData.data.weather_sensors.map((sensor) => {
            const flat = flattenObject(sensor);
            const prefixed: any = {};
            Object.keys(flat).forEach((key) => {
                prefixed[`weather_sensor_${key}`] = flat[key];
            });
            const heading = String(sensor.anemometer_heading);
            prefixed["weather_sensor_anemometer_heading_cardinal"] = degreesToCardinal[heading] ?? heading;
            const mountingArmHeading = String(sensor.mounting_arm_orientation_heading);
            prefixed["weather_sensor_mounting_arm_orientation_heading_cardinal"] = degreesToCardinal[mountingArmHeading] ?? mountingArmHeading;
            return prefixed;
        });

        // Create a map by site_id
        const siteMap = new Map<string, any>();

        // Add all devices to the map
        devicesFlat.forEach((device) => {
            const siteId = device.edge_device_site_id || "";
            if (!siteMap.has(siteId)) {
                siteMap.set(siteId, { site_id: siteId });
            }
            Object.assign(siteMap.get(siteId), device);
        });

        // Add all weather sensors to the map
        weatherSensorsFlat.forEach((sensor) => {
            const siteId = sensor.weather_sensor_site_id || "";
            if (!siteMap.has(siteId)) {
                siteMap.set(siteId, { site_id: siteId });
            }
            Object.assign(siteMap.get(siteId), sensor);
        });

        // Convert map to array
        const allRecords = Array.from(siteMap.values());

        if (allRecords.length === 0) {
            alert("No device data to export");
            return;
        }

        // Sort records by site_id (case-insensitive, numeric-aware)
        allRecords.sort((a, b) => {
            const siteA = String(a.site_id || "").toLowerCase();
            const siteB = String(b.site_id || "").toLowerCase();
            return siteA.localeCompare(siteB, undefined, { numeric: true, sensitivity: "base" });
        });

        // Fixed column order and display name mapping
        const columnDisplayNames: Record<string, string> = {
            "site_id": "Site Number",
            "edge_device_edge_device_id": "Edge Device ID",
            "edge_device_start_tower": "Start Tower Number",
            "edge_device_end_tower": "End Tower Number",
            "edge_device_approx_angle": "Lidar Approximate Angle",
            "edge_device_approx_angle_cardinal": "Lidar Cardinal Direction",
            "edge_device_call_schedule.interval": "Edge Device Call Schedule",
            "edge_device_call_schedule.offset": "Edge Device Call Schedule Offset",
            "edge_device_cloudgate_serial_no": "Edge Device Cloudgate Serial",
            "edge_device_device_type": "Edge Device Device Type",
            "edge_device_install_date": "Edge Device Install Date",
            "edge_device_lidar_id": "Lidar ID",
            "edge_device_lidar_serial_number": "Lidar Serial",
            "edge_device_lidar_type": "Lidar Type",
            "edge_device_monitor_site_id": "Monitor Site ID",
            "edge_device_position.latitude": "Edge Device Lat",
            "edge_device_position.longitude": "Edge Device Lon",
            "edge_device_section_name": "Edge Device Section Name",
            "edge_device_site_id": "Edge Device Site Number",
            "edge_device_visible_spans": "Edge Device Visible Spans",
            "weather_sensor_anemometer_heading": "Weather Sensor Heading",
            "weather_sensor_anemometer_heading_cardinal": "Weather Sensor Heading Cardinal",
            "weather_sensor_device_sn": "Weather Sensor Device SN",
            "weather_sensor_device_type": "Weather Sensor Device Type",
            "weather_sensor_elevation": "Weather Sensor Elevation",
            "weather_sensor_end_dttm": "Weather Sensor End Dttm",
            "weather_sensor_height_above_ground": "Weather Sensor Height Above Ground",
            "weather_sensor_mounting_arm_length": "Weather Sensor Mounting Arm Length",
            "weather_sensor_mounting_arm_orientation_heading": "Weather Sensor Mounting Arm Heading",
            "weather_sensor_mounting_arm_orientation_heading_cardinal": "Weather Sensor Mounting Arm Heading Cardinal",
            "weather_sensor_position.latitude": "Weather Sensor Lat",
            "weather_sensor_position.longitude": "Weather Sensor Lon",
            "weather_sensor_site_id": "Weather Sensor Site Number",
            "weather_sensor_start_dttm": "Weather Sensor Start Dttm",
            "weather_sensor_structure_name": "Weather Sensor Structure Name",
            "weather_sensor_weather_sensor_id": "Weather Sensor Sensor ID",
            "weather_sensor_weather_sensor_install_id": "Weather Sensor Install ID",
        };
        const headers = Object.keys(columnDisplayNames);

        // Create CSV content
        const csvRows = [
            headers.map((h) => columnDisplayNames[h]).join(","), // Header row with display names
            ...allRecords.map((record) =>
                headers.map((header) => {
                    const value = record[header] ?? "";
                    const stringValue = String(value);
                    // Escape quotes and wrap in quotes if contains comma, quote, or newline
                    if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
                        return `"${stringValue.replace(/"/g, '""')}"`;
                    }
                    return stringValue;
                }).join(",")
            ),
        ];

        const csvContent = csvRows.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        const timestamp = new Date().toISOString().split("T")[0];
        const filename = `device-data_${currentClient}_${currentProject}_${timestamp}.csv`;

        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [deviceData, currentClient, currentProject]);

    return (
        <div className={classes.appContainer}>
            <h1>Device Install Ingress</h1>
            <div className={classes.dropdownContainer}>
                <ClientsDropdown
                    value={useAppSelector(selectCurrentClient)}
                    setValue={useCallback((value: string) => dispatch(setClient(value)), [dispatch, setClient])}
                    setError={useCallback((error: unknown) => console.log("ClientsDropdown error: ", error), [])}
                    styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                />
                <ProjectsDropdown
                    clientName={useAppSelector(selectCurrentClient)}
                    value={useAppSelector(selectCurrentProject)}
                    setValue={useCallback((value: string) => dispatch(setProject(value)), [dispatch, setProject])}
                    setError={useCallback((error: unknown) => console.log("ProjectsDropdown error: ", error), [])}
                    styleClass={{ flex: 1, minWidth: "150px", maxWidth: "300px" }}
                />
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExportCSV}
                    disabled={!currentClient || !currentProject || !deviceData}
                    style={{ textTransform: "none", fontSize: "0.75rem", padding: "4px 4px", height: "42px", maxHeight: "62px", marginTop: "15px" }}
                >
                    Export Device Data
                </Button>
                <OnlineDateOverrideInput className={classes.onlineDateOverrideInput} />
            </div>
            <div className={classes.contentContainer}>
                <ClientAssetDataAndDevicesList className={classes.listPanel} />
                <ClientAssetDataAndDeviceMapView className={classes.mapPanel} />
                <DetailedViewControlPanel className={classes.controlPanel} />
            </div>
        </div>
    );
}
